import { z } from 'zod'

export const JourneyChronologyEntrySchema = z.object({
  seq: z.number().int().nonnegative(),
  timestamp: z.string().datetime(),
  kind: z.enum(['step', 'attempt', 'result', 'observation', 'history', 'decision']),
  step: z.number().int().positive().optional(),
  recovery: z.boolean().optional(),
  substep: z.boolean().optional(),
  description: z.string(),
  result: z.string().optional(),
  url: z.string().optional(),
  context: z.string().optional(),
  layout: z.string().optional(),
  structure: z.string().optional(),
  actionIds: z.array(z.string().min(1)),
  operations: z.array(z.object({ action: z.string(), result: z.string(), page_diff: z.string().optional() })).optional(),
})
export type JourneyChronologyEntry = z.infer<typeof JourneyChronologyEntrySchema>
export type JourneyChronologyInput = Omit<JourneyChronologyEntry, 'seq' | 'timestamp'>
export const JourneyChronologyResponseSchema = z.object({
  entries: z.array(JourneyChronologyEntrySchema),
})

/** The prompt is a compact view of the same events persisted for the journey timeline. */
export function renderJourneyChronology(entries: readonly JourneyChronologyEntry[], options: { maxEntries?: number; includeLayout?: boolean } = {}): string {
  const rows: JourneyChronologyEntry[] = []
  for (const entry of entries) {
    if (entry.kind === 'decision' || entry.kind === 'step') continue
    if (entry.kind === 'observation') {
      const previous = rows.at(-1)
      if (previous && previous.step === entry.step && previous.context === entry.context) {
        previous.structure = entry.structure
        previous.layout = entry.layout ?? previous.layout
      }
      continue
    }
    if (entry.kind === 'result') {
      const attempt = [...rows].reverse().find(row => row.kind === 'attempt' && row.step === entry.step
        && row.context === entry.context && row.recovery === entry.recovery && row.description === entry.description)
      if (attempt) rows.splice(rows.indexOf(attempt), 1)
    }
    if (entry.kind === 'history' && entry.substep) {
      const execution = [...rows].reverse().find(row => row.kind === 'result'
        && row.step === entry.step && row.context === entry.context
        && row.actionIds.some(id => entry.actionIds.includes(id)))
      if (execution) {
        execution.result = entry.result ?? execution.result
        execution.structure = entry.structure ?? execution.structure
        execution.layout = entry.layout ?? execution.layout
        continue
      }
    }
    rows.push({ ...entry })
  }
  if (!rows.length) return '(no actions performed yet)'
  return rows.slice(-(options.maxEntries ?? 24)).map(entry => {
    const label = entry.step ? `Step ${entry.step}` : 'Setup'
    const result = entry.kind === 'attempt' ? 'started; no result recorded yet' : entry.result
    // Inbox bodies and verification URLs remain available through the inbox capability, not repeated
    // in every subsequent prompt. Preserve the link reference and execution outcome instead.
    const summary = entry.description === 'Read email inbox' && result?.startsWith('Email inbox:')
      ? (result.includes('Email 1:') ? 'email received (links available from inbox)' : 'no matching email received')
      : result?.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]')
    const page = entry.structure?.split('\n').filter(line => line.startsWith('· ')).slice(0, 12).join(' ')
      || (entry.structure && /LoaderCircle|AnimateSpin/.test(entry.structure) ? 'loading indicator present' : '')
    if (entry.operations !== undefined) {
      return `[${label}${entry.recovery ? ' recovery' : ''}${entry.context ? `; ${entry.context}` : ''}] Requested step action: ${entry.description}\n`
        + (entry.operations.length ? entry.operations.map(operation => `${operation.action} → ${operation.result}`
          + (operation.page_diff ? `\nObserved page change after this operation:\n${operation.page_diff}` : '')).join('\n')
          + (summary ? `\nAttempt result: ${summary}` : '')
          + (entry.operations.every(operation => operation.page_diff === undefined) && entry.layout ? `\nObserved page change after this action:\n${entry.layout}` : '')
          : `No browser operations executed. ${summary ?? ''}`)
    }
    return `[${label}${entry.recovery ? ' recovery' : ''}${entry.context ? `; ${entry.context}` : ''}] `
      + entry.description.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]')
      + (summary ? ` → ${summary.slice(0, 500)}` : '')
      + (page ? ` | Observed: ${page.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]').slice(0, 300)}` : '')
      + (options.includeLayout && entry.layout ? `\n${entry.layout.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]')}` : '')
  }).join('\n')
}
